import { useState, useEffect } from 'react';
import { useAuth } from '../features/auth/AuthContext';
import {
  getTemplates,
  getTemplateVersions,
  getTemplateVersion,
  createTemplate,
  updateTemplate,
  createTemplateVersion,
  updateDraftVersion,
  publishTemplateVersion,
  extractVariables
} from '../features/templates/api/templatesApi';

const CATEGORIES = [
  { value: 'legal', label: 'Legal Documents' },
  { value: 'email', label: 'Email Templates' },
  { value: 'notification', label: 'Notifications' },
  { value: 'general', label: 'General' }
];

function TemplateList({ templates, selectedId, onSelect, onCreateNew }) {
  const grouped = templates.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-secondary-200">
        <button onClick={onCreateNew} className="btn-primary w-full text-sm">
          New Template
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {CATEGORIES.map(cat => {
          const items = grouped[cat.value] || [];
          if (items.length === 0) return null;
          return (
            <div key={cat.value} className="py-2">
              <h3 className="px-4 py-2 text-xs font-semibold text-secondary-500 uppercase tracking-wide">
                {cat.label}
              </h3>
              {items.map(t => (
                <button
                  key={t.id}
                  onClick={() => onSelect(t)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-secondary-50 ${
                    selectedId === t.id ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600' : 'text-secondary-700'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VersionHistory({ versions, selectedVersionId, onSelectVersion }) {
  if (!versions.length) return null;

  return (
    <div className="border-t border-secondary-200 p-4">
      <h3 className="text-sm font-semibold text-secondary-900 mb-3">Version History</h3>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {versions.map(v => (
          <button
            key={v.id}
            onClick={() => onSelectVersion(v)}
            className={`w-full text-left p-2 rounded text-sm ${
              selectedVersionId === v.id
                ? 'bg-primary-50 border border-primary-200'
                : 'hover:bg-secondary-50 border border-transparent'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium">v{v.version_number}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                v.status === 'published' ? 'bg-green-100 text-green-700' :
                v.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                'bg-secondary-100 text-secondary-600'
              }`}>
                {v.status}
              </span>
            </div>
            {v.change_notes && (
              <p className="text-xs text-secondary-500 mt-1 truncate">{v.change_notes}</p>
            )}
            <p className="text-xs text-secondary-400 mt-1">
              {new Date(v.created_at).toLocaleDateString()}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function VariablesPanel({ variables, onUpdate }) {
  const [newVar, setNewVar] = useState({ name: '', description: '' });

  function handleAdd() {
    if (!newVar.name.trim()) return;
    const slug = newVar.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    onUpdate([...variables, { name: slug, description: newVar.description }]);
    setNewVar({ name: '', description: '' });
  }

  function handleRemove(index) {
    onUpdate(variables.filter((_, i) => i !== index));
  }

  function handleUpdateDescription(index, description) {
    const updated = [...variables];
    updated[index] = { ...updated[index], description };
    onUpdate(updated);
  }

  return (
    <div className="p-4">
      <h3 className="text-sm font-semibold text-secondary-900 mb-3">
        Template Variables
      </h3>
      <p className="text-xs text-secondary-500 mb-3">
        Use [[variable_name]] syntax in template content
      </p>
      <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
        {variables.map((v, i) => (
          <div key={i} className="flex items-start gap-2 p-2 bg-secondary-50 rounded text-sm">
            <div className="flex-1">
              <code className="text-xs bg-secondary-200 px-1 rounded">[[{v.name}]]</code>
              <input
                type="text"
                value={v.description}
                onChange={(e) => handleUpdateDescription(i, e.target.value)}
                placeholder="Description..."
                className="mt-1 w-full text-xs border-0 bg-transparent focus:ring-0 p-0 text-secondary-600"
              />
            </div>
            <button
              onClick={() => handleRemove(i)}
              className="text-secondary-400 hover:text-red-500"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={newVar.name}
          onChange={(e) => setNewVar({ ...newVar, name: e.target.value })}
          placeholder="variable_name"
          className="input-field text-xs flex-1"
        />
        <button onClick={handleAdd} className="btn-outline text-xs px-3">
          Add
        </button>
      </div>
    </div>
  );
}

function CreateTemplateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    category: 'general'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleNameChange(name) {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setForm({ ...form, name, slug });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.slug) return;

    setLoading(true);
    setError('');

    try {
      const template = await createTemplate(form);
      onCreated(template);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">New Template</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="input-field"
                required
              />
              <p className="text-xs text-secondary-500 mt-1">URL-friendly identifier</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="input-field"
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="input-field"
                rows={2}
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Creating...' : 'Create Template'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminTemplatesPage() {
  const { profile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [content, setContent] = useState('');
  const [variables, setVariables] = useState([]);
  const [changeNotes, setChangeNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    try {
      const data = await getTemplates();
      setTemplates(data || []);
      if (data?.length && !selectedTemplate) {
        handleSelectTemplate(data[0]);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectTemplate(template) {
    if (hasChanges && !confirm('You have unsaved changes. Continue?')) return;

    setSelectedTemplate(template);
    setHasChanges(false);
    setChangeNotes('');

    try {
      const vers = await getTemplateVersions(template.id);
      setVersions(vers || []);

      if (template.current_version_id) {
        const currentVer = vers?.find(v => v.id === template.current_version_id) || vers?.[0];
        if (currentVer) {
          handleSelectVersion(currentVer);
        }
      } else if (vers?.length) {
        handleSelectVersion(vers[0]);
      } else {
        setSelectedVersion(null);
        setContent('');
        setVariables([]);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load template versions' });
    }
  }

  function handleSelectVersion(version) {
    setSelectedVersion(version);
    setContent(version.content);
    setVariables(version.variables || []);
    setHasChanges(false);
    setChangeNotes(version.status === 'draft' ? version.change_notes || '' : '');
  }

  function handleContentChange(newContent) {
    setContent(newContent);
    setHasChanges(true);
  }

  function handleVariablesChange(newVars) {
    setVariables(newVars);
    setHasChanges(true);
  }

  function handleExtractVariables() {
    const extracted = extractVariables(content);
    const existing = new Set(variables.map(v => v.name));
    const merged = [
      ...variables,
      ...extracted.filter(v => !existing.has(v.name))
    ];
    setVariables(merged);
    setHasChanges(true);
  }

  async function handleSave() {
    if (!selectedTemplate) return;

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      if (selectedVersion?.status === 'draft') {
        await updateDraftVersion(selectedVersion.id, {
          content,
          variables,
          changeNotes
        });
        setMessage({ type: 'success', text: 'Draft saved' });
      } else {
        const newVersion = await createTemplateVersion(selectedTemplate.id, {
          content,
          variables,
          changeNotes
        });
        const vers = await getTemplateVersions(selectedTemplate.id);
        setVersions(vers || []);
        handleSelectVersion(newVersion);
        setMessage({ type: 'success', text: 'New draft version created' });
      }
      setHasChanges(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!selectedVersion || selectedVersion.status !== 'draft') return;

    if (!confirm('Publish this version? It will become the active template.')) return;

    setPublishing(true);
    setMessage({ type: '', text: '' });

    try {
      await publishTemplateVersion(selectedVersion.id);
      const vers = await getTemplateVersions(selectedTemplate.id);
      setVersions(vers || []);
      const published = vers?.find(v => v.id === selectedVersion.id);
      if (published) handleSelectVersion(published);
      await loadTemplates();
      setMessage({ type: 'success', text: 'Template published successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setPublishing(false);
    }
  }

  function handleTemplateCreated(template) {
    setShowCreateModal(false);
    loadTemplates();
    handleSelectTemplate(template);
  }

  if (profile?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-secondary-600">Access denied. Admin only.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="h-screen flex flex-col">
        <header className="bg-white border-b border-secondary-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-secondary-900">Template Manager</h1>
              <p className="text-sm text-secondary-600 mt-1">
                Manage and version control platform templates
              </p>
            </div>
            {message.text && (
              <div className={`px-4 py-2 rounded text-sm ${
                message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}>
                {message.text}
              </div>
            )}
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          <aside className="w-64 bg-white border-r border-secondary-200 flex-shrink-0">
            <TemplateList
              templates={templates}
              selectedId={selectedTemplate?.id}
              onSelect={handleSelectTemplate}
              onCreateNew={() => setShowCreateModal(true)}
            />
          </aside>

          <main className="flex-1 flex flex-col overflow-hidden">
            {selectedTemplate ? (
              <>
                <div className="bg-white border-b border-secondary-200 px-6 py-3 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-secondary-900">
                      {selectedTemplate.name}
                    </h2>
                    <p className="text-sm text-secondary-500">
                      {selectedTemplate.slug} | {selectedTemplate.category}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedVersion && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        selectedVersion.status === 'published' ? 'bg-green-100 text-green-700' :
                        selectedVersion.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-secondary-100 text-secondary-600'
                      }`}>
                        v{selectedVersion.version_number} ({selectedVersion.status})
                      </span>
                    )}
                    <button
                      onClick={handleExtractVariables}
                      className="btn-outline text-sm"
                    >
                      Extract Variables
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || !hasChanges}
                      className="btn-outline text-sm"
                    >
                      {saving ? 'Saving...' : 'Save Draft'}
                    </button>
                    {selectedVersion?.status === 'draft' && (
                      <button
                        onClick={handlePublish}
                        disabled={publishing || hasChanges}
                        className="btn-primary text-sm"
                      >
                        {publishing ? 'Publishing...' : 'Publish'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 flex overflow-hidden">
                  <div className="flex-1 flex flex-col overflow-hidden p-4">
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Change Notes
                      </label>
                      <input
                        type="text"
                        value={changeNotes}
                        onChange={(e) => {
                          setChangeNotes(e.target.value);
                          setHasChanges(true);
                        }}
                        placeholder="Describe your changes..."
                        className="input-field text-sm"
                      />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Template Content
                      </label>
                      <textarea
                        value={content}
                        onChange={(e) => handleContentChange(e.target.value)}
                        className="w-full h-full font-mono text-sm p-4 border border-secondary-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter template content..."
                      />
                    </div>
                  </div>

                  <aside className="w-72 bg-white border-l border-secondary-200 flex-shrink-0 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto">
                      <VariablesPanel
                        variables={variables}
                        onUpdate={handleVariablesChange}
                      />
                    </div>
                    <VersionHistory
                      versions={versions}
                      selectedVersionId={selectedVersion?.id}
                      onSelectVersion={handleSelectVersion}
                    />
                  </aside>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-secondary-500">
                Select a template or create a new one
              </div>
            )}
          </main>
        </div>
      </div>

      {showCreateModal && (
        <CreateTemplateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleTemplateCreated}
        />
      )}
    </div>
  );
}
